// agenda-roadmap.js
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const isEditor =
  document.body.classList.contains("block-editor-page") ||
  document.body.classList.contains("wp-admin");

if (!isEditor) gsap.registerPlugin(ScrollTrigger);

/* ---------------- helpers ---------------- */
function imagesReady(scope, cb) {
  const imgs = Array.from(scope.querySelectorAll("img"));
  if (!imgs.length) return cb();
  let left = imgs.length;
  const done = () => (--left === 0 ? cb() : null);
  imgs.forEach((img) => {
    if (img.complete) return done();
    img.addEventListener("load", done, { once: true });
    img.addEventListener("error", done, { once: true });
  });
}

/**
 * Get center Y of `el` relative to `ancestor` using offset chain (transform-proof).
 */
function centerYRelative(el, ancestor) {
  let y = el.offsetHeight / 2;
  let n = el;
  while (n && n !== ancestor) {
    y += n.offsetTop;
    n = n.offsetParent;
  }
  return y;
}

/**
 * Position the vertical line between the centers of first/last nodes.
 * Uses offset chain so it's immune to CSS transforms and animation.
 */
function positionTimelineLine(section) {
  const wrap = section.querySelector(".agenda__timeline");
  const line = section.querySelector(".agenda__line");
  const nodes = Array.from(section.querySelectorAll(".agenda__node"));
  if (!wrap || !line || nodes.length < 2) {
    if (line) line.style.display = "none";
    return;
  }
  line.style.removeProperty("display");

  const first = nodes[0];
  const last = nodes[nodes.length - 1];

  const top = Math.round(centerYRelative(first, wrap));
  const end = Math.round(centerYRelative(last, wrap));
  const height = Math.max(0, end - top);

  line.style.top = `${top}px`;
  line.style.height = `${height}px`;
}

/* ---------------- main ---------------- */
function initAgenda(section) {
  if (!section || section.__agendaInit) return;
  section.__agendaInit = true;

  const eyebrow = section.querySelector(".agenda__eyebrow");
  const dot = eyebrow?.querySelector(".dot");
  const headline = section.querySelector(".agenda__headline");
  const intro = section.querySelector(".agenda__intro");
  const photos = gsap.utils.toArray(section.querySelectorAll(".agenda__photo"));
  const items = gsap.utils.toArray(section.querySelectorAll(".agenda__item"));
  const nodes = gsap.utils.toArray(
    section.querySelectorAll(".agenda__node-core")
  );

  // initial states
  gsap.set([eyebrow, headline, intro], { opacity: 0, y: 20 });
  gsap.set(photos, { opacity: 0, y: 16, scale: 0.98 });
  gsap.set(items, { opacity: 0, y: 24 });
  if (dot) gsap.set(dot, { scale: 0.6 });

  const play = () => {
    // lock the line BEFORE any movement; do not recompute during animation
    positionTimelineLine(section);

    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

    if (eyebrow) tl.to(eyebrow, { opacity: 1, y: 0, duration: 0.45 });
    if (dot)
      tl.to(dot, { scale: 1, duration: 0.4, ease: "back.out(2.2)" }, "<");
    if (headline)
      tl.to(headline, { opacity: 1, y: 0, duration: 0.75 }, "-=0.2");
    if (intro) tl.to(intro, { opacity: 1, y: 0, duration: 0.55 }, "-=0.3");

    if (photos.length) {
      tl.to(
        photos,
        { opacity: 1, y: 0, scale: 1, duration: 0.55, stagger: 0.1 },
        "-=0.2"
      );
    }

    if (items.length) {
      tl.to(
        items,
        {
          opacity: 1,
          y: 0,
          duration: 0.55,
          stagger: 0.12,
          ease: "power2.out",
          onStart: () =>
            nodes.forEach((n) =>
              gsap.fromTo(
                n,
                { scale: 0.3 },
                { scale: 1, duration: 0.25, ease: "back.out(2)" }
              )
            ),
        },
        "-=0.1"
      );
    }
  };

  const afterImages = () => {
    if (isEditor) {
      play();
      // also set line once in editor in case of late layout changes
      positionTimelineLine(section);
      return;
    }

    // Frontend
    ScrollTrigger.create({
      trigger: section,
      start: "top 78%",
      once: true,
      onEnter: play,
    });

    // pre-position to avoid flash
    positionTimelineLine(section);
    ScrollTrigger.refresh();
  };

  imagesReady(section, afterImages);

  // Only recompute on viewport changes (debounced), not during animation
  let raf;
  const onResize = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => positionTimelineLine(section));
  };
  window.addEventListener("resize", onResize);
  window.addEventListener("orientationchange", onResize);
}

function mount() {
  document.querySelectorAll(".agenda-roadmap").forEach(initAgenda);

  if (isEditor) {
    const root = document.querySelector(".editor-styles-wrapper") || document;
    const mo = new MutationObserver(() =>
      root.querySelectorAll(".agenda-roadmap").forEach(initAgenda)
    );
    mo.observe(root, { childList: true, subtree: true });
  }
}

if (window.wp?.domReady) window.wp.domReady(mount);
else document.addEventListener("DOMContentLoaded", mount);
